import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";
import { Controller } from "react-hook-form";
import TextField from "../../../components/Form/TextField";
import Select from "../../../components/Form/Select";
import Switch from "../../../components/Form/Switch";
import ColorField from "../../../components/Form/ColorField";
import Button from "../../../components/Buttons/Button";
import FontFamilySelect from "./FontFamilySelect";
import FontFaceSelect from "./FontFaceSelect";
import TextStyleSelect from "./TextStyleSelect";
import TextOrientationSelect from "./TextOrientationSelect";
import TextStrokeFields from "./TextStrokeFields";
import TextShadowFields from "./TextShadowFields";
import TextBackgroundFields from "./TextBackgroundFields";
import TextSpacingFields from "./TextSpacingFields";
import TextHighlightsFields from "./TextHighlightsFields";
import TextInlineReplacementsFields from "./TextInlineReplacementsFields";
import LayerAlignToolbar from "./LayerAlignToolbar";
import PromptInstructionField from "./PromptInstructionField";
import { isTextOriginPreviewKind } from "../utils/layerTree";
import {
  canHighlightTextSource,
  highlightSourceText,
} from "../utils/textHighlights";
import {
  isVerticalTextOrientation,
  resolveTextOrientation,
} from "../utils/textOrientation";
import { useTranslationApp } from "../../../lib/i18next";
import {
  TEMPLATES_ALIGN,
  TEMPLATES_ALIGN_CENTER,
  TEMPLATES_ALIGN_LEFT,
  TEMPLATES_ALIGN_RIGHT,
  TEMPLATES_ASSET_REF,
  TEMPLATES_CHANGE_ASSET,
  TEMPLATES_CLIP,
  TEMPLATES_EXPRESSION,
  TEMPLATES_LAYER_TEXT,
  TEMPLATES_STYLE,
  TEMPLATES_FIXED_VALUE_PLACEHOLDER,
  TEMPLATES_ORIGIN_TEXT,
  TEMPLATES_FONT_COLOR,
  TEMPLATES_FONT_SIZE,
  TEMPLATES_FRAME_H,
  TEMPLATES_FRAME_W,
  TEMPLATES_LAYER_NAME,
  TEMPLATES_MAX_LINES,
  TEMPLATES_MIN_LINES,
  TEMPLATES_NO_SELECTION,
  TEMPLATES_POS_H,
  TEMPLATES_POS_W,
  TEMPLATES_POS_X,
  TEMPLATES_POS_Y,
  TEMPLATES_POSITION,
  TEMPLATES_TRY_LAYER,
  TEMPLATES_IS_BACKGROUND,
  TEMPLATES_SELECT_ASSET,
  TEMPLATES_SIZE,
  TEMPLATES_PROPERTIES,
  TEMPLATES_SOURCE_ASSET,
  TEMPLATES_SOURCE_COMPUTED,
  TEMPLATES_SOURCE_DATA,
  TEMPLATES_SOURCE_GENERATED,
  TEMPLATES_SOURCE_ASSET_HINT,
  TEMPLATES_SOURCE_COMPUTED_HINT,
  TEMPLATES_SOURCE_DATA_HINT,
  TEMPLATES_SOURCE_GENERATED_HINT,
  TEMPLATES_TYPE_GROUP,
  TEMPLATES_TYPE_IMAGE,
  TEMPLATES_TYPE_SHAPE,
  TEMPLATES_TYPE_TEXT,
  TEMPLATES_ZONE_SOURCE,
  TEMPLATES_ZONE_TYPE,
  YES,
  NO,
} from "../../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import {
  RESIZABLE_MULTILINE_MIN_ROWS,
  resizableMultilineSx,
} from "./canvasWorkspace";

const optionalRules = { required: false };

const FieldGroup = ({ title, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, color: "text.secondary", fontSize: "13px" }}
    >
      {title}
    </Typography>
    <Box sx={{ display: "flex", gap: 1 }}>{children}</Box>
  </Box>
);

const propertiesCardSx = {
  bgcolor: "#fff",
  border: "1px solid #E4E4E7",
  borderRadius: "14px",
  p: 2,
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  height: "100%",
  minHeight: 0,
  overflow: "auto",
};

const LayerPropertiesPanel = ({
  control,
  errors,
  disabled,
  selectedNode,
  isRootLayer = false,
  assetUrl = "",
  nameSuggestions = [],
  isTextRangeSelected = false,
  onChangeField,
  onSelectAsset = () => {},
  onSelectInlineReplacementAsset = () => {},
  inlineReplacementUrls = {},
  onTryGenerate = () => {},
  isTryingGenerate = false,
  onGeneratePrompt,
  isGeneratingPrompt = false,
  originImageUrl = "",
  alignOnly = false,
  onAlign,
}) => {
  const { t } = useTranslationApp();
  const type = selectedNode?.type;
  const sourceKind = selectedNode?.source?.kind;
  const isHorizontalText =
    type === "text" &&
    !isVerticalTextOrientation(
      resolveTextOrientation(selectedNode?.style?.orientation)
    );
  const isCanvasBackground = Boolean(selectedNode?.isBackground);
  const showBackgroundCheckbox =
    isRootLayer &&
    type === "image" &&
    (sourceKind === "generated" || sourceKind === "asset");
  const geometryDisabled = disabled || isCanvasBackground;

  const sourceOptions =
    type === "text"
      ? [
          {
            value: "fixed",
            label: t(TEMPLATES_SOURCE_ASSET),
            tooltip: t(TEMPLATES_SOURCE_ASSET_HINT),
          },
          {
            value: "data",
            label: t(TEMPLATES_SOURCE_DATA),
            tooltip: t(TEMPLATES_SOURCE_DATA_HINT),
          },
          {
            value: "generated",
            label: t(TEMPLATES_SOURCE_GENERATED),
            tooltip: t(TEMPLATES_SOURCE_GENERATED_HINT),
          },
          ...(sourceKind === "computed"
            ? [
                {
                  value: "computed",
                  label: t(TEMPLATES_SOURCE_COMPUTED),
                  tooltip: t(TEMPLATES_SOURCE_COMPUTED_HINT),
                },
              ]
            : []),
        ]
      : type === "image"
        ? [
            {
              value: "asset",
              label: t(TEMPLATES_SOURCE_ASSET),
              tooltip: t(TEMPLATES_SOURCE_ASSET_HINT),
            },
            {
              value: "data",
              label: t(TEMPLATES_SOURCE_DATA),
              tooltip: t(TEMPLATES_SOURCE_DATA_HINT),
            },
            {
              value: "generated",
              label: t(TEMPLATES_SOURCE_GENERATED),
              tooltip: t(TEMPLATES_SOURCE_GENERATED_HINT),
            },
          ]
        : [];

  const typeOptions = [
    { value: "text", label: t(TEMPLATES_TYPE_TEXT) },
    { value: "image", label: t(TEMPLATES_TYPE_IMAGE) },
    ...(type === "group"
      ? [{ value: "group", label: t(TEMPLATES_TYPE_GROUP) }]
      : []),
    ...(type === "shape"
      ? [{ value: "shape", label: t(TEMPLATES_TYPE_SHAPE) }]
      : []),
  ];

  if (alignOnly) {
    return (
      <Box sx={propertiesCardSx}>
        <Typography sx={{ fontWeight: 500 }}>
          {t(TEMPLATES_PROPERTIES)}
        </Typography>
        <LayerAlignToolbar disabled={disabled} onAlign={onAlign} />
      </Box>
    );
  }

  if (!selectedNode) {
    return (
      <Box sx={propertiesCardSx}>
        <Typography sx={{ fontWeight: 500 }}>
          {t(TEMPLATES_PROPERTIES)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(TEMPLATES_NO_SELECTION)}
        </Typography>
      </Box>
    );
  }

  // The only controls a highlight can override. They are shared verbatim with
  // the reduced panel below, so both views can never drift apart.
  const highlightableFontFields = (
    <>
      <FontFamilySelect
        control={control}
        errors={errors}
        disabled={disabled}
        onChangeField={onChangeField}
      />
      <FontFaceSelect
        control={control}
        errors={errors}
        disabled={disabled}
        onChangeField={onChangeField}
      />
      <TextStyleSelect
        control={control}
        errors={errors}
        disabled={disabled}
        onChangeField={onChangeField}
        label={t(TEMPLATES_STYLE)}
      />
    </>
  );
  const highlightablePaintFields = (
    <>
      <ColorField
        control={control}
        errors={errors}
        name="color"
        label={t(TEMPLATES_FONT_COLOR)}
        fullWidth
        disabled={disabled}
        rules={optionalRules}
        actionsChange={(value) => onChangeField({ key: "color", value })}
      />
      <TextStrokeFields
        control={control}
        errors={errors}
        disabled={disabled}
        onChangeField={onChangeField}
      />
      <TextBackgroundFields
        control={control}
        errors={errors}
        disabled={disabled}
        onChangeField={onChangeField}
      />
    </>
  );
  // Only data-driven text gets the summary list: each highlight there becomes
  // a produce column the user has to fill in, so it needs to be reviewable.
  // Fixed text and the edit modals apply highlights straight on the canvas,
  // where the list would just be noise.
  const textHighlightsSection =
    sourceKind === "data" ? (
      <TextHighlightsFields
        highlights={selectedNode?.style?.highlights}
        text={highlightSourceText(selectedNode)}
        disabled={disabled}
        onChangeHighlights={(value) =>
          onChangeField({ key: "highlights", value })
        }
      />
    ) : null;

  const textInlineReplacementsSection = isHorizontalText ? (
    <TextInlineReplacementsFields
      key={selectedNode.id}
      replacements={selectedNode?.style?.inlineReplacements}
      urls={inlineReplacementUrls}
      disabled={disabled}
      onChangeReplacements={(value) =>
        onChangeField({ key: "inlineReplacements", value })
      }
      onSelectAsset={onSelectInlineReplacementAsset}
    />
  ) : null;

  // A selected range styles only that phrase, so everything that can only
  // apply to the whole layer is hidden instead of silently doing nothing.
  if (
    isTextRangeSelected &&
    type === "text" &&
    canHighlightTextSource(sourceKind)
  ) {
    return (
      <Box sx={propertiesCardSx}>
        <Typography sx={{ fontWeight: 500 }}>
          {t(TEMPLATES_PROPERTIES)}
        </Typography>
        {highlightableFontFields}
        {highlightablePaintFields}
        {textHighlightsSection}
      </Box>
    );
  }

  return (
    <Box sx={propertiesCardSx}>
      <Typography sx={{ fontWeight: 500 }}>
        {t(TEMPLATES_PROPERTIES)}
      </Typography>
      <Controller
        control={control}
        name="layerName"
        render={({ field }) => (
          <Autocomplete
            freeSolo
            options={nameSuggestions}
            value={field.value || ""}
            inputValue={field.value || ""}
            disabled={disabled}
            hasLabel
            onChange={(_, value) => {
              const next = value == null ? "" : String(value);
              field.onChange(next);
              onChangeField({ key: "name", value: next });
            }}
            onInputChange={(_, value, reason) => {
              if (reason !== "input" && reason !== "clear") return;
              field.onChange(value);
              onChangeField({ key: "name", value });
            }}
            renderInput={(params) => (
              <MuiTextField
                {...params}
                label={t(TEMPLATES_LAYER_NAME)}
                variant="filled"
                fullWidth
                InputLabelProps={{
                  ...params.InputLabelProps,
                  shrink: true,
                }}
                sx={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                }}
              />
            )}
          />
        )}
      />
      <Select
        control={control}
        errors={errors}
        name="type"
        label={t(TEMPLATES_ZONE_TYPE)}
        variant="filled"
        fullWidth
        disabled
        rules={optionalRules}
        actionsChange={(value) => onChangeField({ key: "type", value })}
        options={typeOptions}
      />
      {sourceOptions.length ? (
        <Select
          control={control}
          errors={errors}
          name="sourceKind"
          label={t(TEMPLATES_ZONE_SOURCE)}
          variant="filled"
          fullWidth
          disabled={disabled}
          rules={optionalRules}
          actionsChange={(value) => onChangeField({ key: "sourceKind", value })}
          options={sourceOptions}
        />
      ) : null}
      {sourceKind === "fixed" ? (
        <TextField
          control={control}
          errors={errors}
          name="fixedValue"
          label={t(TEMPLATES_LAYER_TEXT)}
          placeholder={t(TEMPLATES_FIXED_VALUE_PLACEHOLDER)}
          variant="filled"
          fullWidth
          multiline
          minRows={RESIZABLE_MULTILINE_MIN_ROWS}
          disabled={disabled}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          sx={resizableMultilineSx}
          actionsChange={(value) => onChangeField({ key: "fixedValue", value })}
        />
      ) : null}
      {type === "text" && isTextOriginPreviewKind(sourceKind) ? (
        <TextField
          control={control}
          errors={errors}
          name="originText"
          label={t(TEMPLATES_ORIGIN_TEXT)}
          variant="filled"
          fullWidth
          multiline
          minRows={RESIZABLE_MULTILINE_MIN_ROWS}
          disabled={disabled}
          rules={optionalRules}
          sx={resizableMultilineSx}
          actionsChange={(value) => onChangeField({ key: "originText", value })}
        />
      ) : null}
      {sourceKind === "computed" ? (
        <TextField
          control={control}
          errors={errors}
          name="expression"
          label={t(TEMPLATES_EXPRESSION)}
          variant="filled"
          fullWidth
          multiline
          minRows={2}
          disabled={disabled}
          rules={optionalRules}
          actionsChange={(value) => onChangeField({ key: "expression", value })}
        />
      ) : null}
      {sourceKind === "generated" ? (
        <>
          <PromptInstructionField
            control={control}
            errors={errors}
            disabled={disabled}
            nodeID={selectedNode?.id || ""}
            kind={type === "text" ? "text" : "image"}
            originText={selectedNode?.originText || ""}
            originImageUrl={originImageUrl}
            promptValue={selectedNode?.source?.promptTemplate || ""}
            onChangeField={onChangeField}
            onGenerate={onGeneratePrompt}
            isGenerating={isGeneratingPrompt}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              color="secondary"
              disabled={disabled || isTryingGenerate}
              loading={isTryingGenerate}
              onClick={onTryGenerate}
              sx={{ minWidth: 88, height: 32, py: "4px" }}
            >
              {t(TEMPLATES_TRY_LAYER)}
            </Button>
          </Box>
        </>
      ) : null}
      {showBackgroundCheckbox ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {t(TEMPLATES_IS_BACKGROUND)}
          </Typography>
          <Switch
            id="layer-background-switch"
            isActive={isCanvasBackground}
            disabled={disabled}
            size="small"
            hasLabel={false}
            onChangeIsActive={(checked) =>
              onChangeField({
                key: "isBackground",
                value: checked,
              })
            }
          />
        </Box>
      ) : null}
      {sourceKind === "asset" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: "text.secondary", fontSize: "13px" }}
          >
            {t(TEMPLATES_ASSET_REF)}
          </Typography>
          {assetUrl ? (
            <Box
              onClick={disabled ? undefined : onSelectAsset}
              sx={{
                width: "100%",
                aspectRatio: "1 / 1",
                borderRadius: 2,
                border: "1px solid #E4E4E7",
                bgcolor: "action.hover",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: disabled ? "default" : "pointer",
              }}
            >
              <Box
                component="img"
                src={assetUrl}
                alt=""
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : null}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              color="secondary"
              disabled={disabled}
              onClick={onSelectAsset}
            >
              {t(assetUrl ? TEMPLATES_CHANGE_ASSET : TEMPLATES_SELECT_ASSET)}
            </Button>
          </Box>
        </Box>
      ) : null}
      {type === "text" ? (
        <>
          {highlightableFontFields}
          <TextField
            control={control}
            errors={errors}
            name="fontSize"
            label={t(TEMPLATES_FONT_SIZE)}
            variant="filled"
            type="number"
            fullWidth
            disabled={disabled}
            rules={optionalRules}
            InputLabelProps={{ shrink: true }}
            actionsChange={(value) =>
              onChangeField({
                key: "fontSize",
                value: value === "" ? undefined : Number(value),
              })
            }
          />
          <TextSpacingFields
            control={control}
            errors={errors}
            disabled={disabled}
            onChangeField={onChangeField}
          />
          {highlightablePaintFields}
          <TextShadowFields
            control={control}
            errors={errors}
            disabled={disabled}
            onChangeField={onChangeField}
          />
          <Select
            control={control}
            errors={errors}
            name="align"
            label={t(TEMPLATES_ALIGN)}
            variant="filled"
            fullWidth
            disabled={disabled}
            rules={optionalRules}
            actionsChange={(value) => onChangeField({ key: "align", value })}
            options={[
              { value: "left", label: t(TEMPLATES_ALIGN_LEFT) },
              { value: "center", label: t(TEMPLATES_ALIGN_CENTER) },
              { value: "right", label: t(TEMPLATES_ALIGN_RIGHT) },
            ]}
          />
          <TextOrientationSelect
            control={control}
            errors={errors}
            disabled={disabled}
            onChangeField={onChangeField}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              control={control}
              errors={errors}
              name="minimumLines"
              label={t(TEMPLATES_MIN_LINES)}
              variant="filled"
              type="number"
              fullWidth
              disabled={disabled}
              rules={optionalRules}
              InputLabelProps={{ shrink: true }}
              actionsChange={(value) =>
                onChangeField({
                  key: "minimumLines",
                  value: value === "" ? undefined : Number(value),
                })
              }
            />
            <TextField
              control={control}
              errors={errors}
              name="maximumLines"
              label={t(TEMPLATES_MAX_LINES)}
              variant="filled"
              type="number"
              fullWidth
              disabled={disabled}
              rules={optionalRules}
              InputLabelProps={{ shrink: true }}
              actionsChange={(value) =>
                onChangeField({
                  key: "maximumLines",
                  value: value === "" ? undefined : Number(value),
                })
              }
            />
          </Box>
        </>
      ) : null}
      {type === "group" ? (
        <>
          <TextField
            control={control}
            errors={errors}
            name="frameWidth"
            label={t(TEMPLATES_FRAME_W)}
            variant="filled"
            type="number"
            fullWidth
            disabled={disabled}
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeField({
                key: "frameWidth",
                value: value === "" ? undefined : Number(value),
              })
            }
          />
          <TextField
            control={control}
            errors={errors}
            name="frameHeight"
            label={t(TEMPLATES_FRAME_H)}
            variant="filled"
            type="number"
            fullWidth
            disabled={disabled}
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeField({
                key: "frameHeight",
                value: value === "" ? undefined : Number(value),
              })
            }
          />
          <Select
            control={control}
            errors={errors}
            name="clipContent"
            label={t(TEMPLATES_CLIP)}
            variant="filled"
            fullWidth
            disabled={disabled}
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeField({ key: "clipContent", value: value === "true" })
            }
            options={[
              { value: "true", label: t(YES) },
              { value: "false", label: t(NO) },
            ]}
          />
        </>
      ) : null}
      <FieldGroup title={t(TEMPLATES_POSITION)}>
        <TextField
          control={control}
          errors={errors}
          name="posX"
          label={t(TEMPLATES_POS_X)}
          variant="filled"
          type="number"
          fullWidth
          disabled={geometryDisabled}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeField({
              key: "posX",
              value: value === "" ? undefined : Number(value),
            })
          }
        />
        <TextField
          control={control}
          errors={errors}
          name="posY"
          label={t(TEMPLATES_POS_Y)}
          variant="filled"
          type="number"
          fullWidth
          disabled={geometryDisabled}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeField({
              key: "posY",
              value: value === "" ? undefined : Number(value),
            })
          }
        />
      </FieldGroup>
      <FieldGroup title={t(TEMPLATES_SIZE)}>
        <TextField
          control={control}
          errors={errors}
          name="posW"
          label={t(TEMPLATES_POS_W)}
          variant="filled"
          type="number"
          fullWidth
          disabled={geometryDisabled}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeField({
              key: "posW",
              value: value === "" ? undefined : Number(value),
            })
          }
        />
        <TextField
          control={control}
          errors={errors}
          name="posH"
          label={t(TEMPLATES_POS_H)}
          variant="filled"
          type="number"
          fullWidth
          disabled={geometryDisabled}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeField({
              key: "posH",
              value: value === "" ? undefined : Number(value),
            })
          }
        />
      </FieldGroup>
      {type === "text" ? textHighlightsSection : null}
      {textInlineReplacementsSection}
    </Box>
  );
};

export default LayerPropertiesPanel;
